// src/components/controlPanel/OverrideEditModal.jsx
import { useState, useEffect, useMemo } from "react";

const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    const hh = String(h).padStart(2, "0");
    const mm = String(m).padStart(2, "0");
    TIME_OPTIONS.push(`${hh}:${mm}`);
  }
}

function TimeSelect({ value, onChange }) {
  return (
    <select
      value={value || "09:00"}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:outline-none transition bg-white"
    >
      {TIME_OPTIONS.map((t) => (
        <option key={t} value={t}>
          {t}
        </option>
      ))}
    </select>
  );
}

export default function OverrideEditModal({
  isOpen,
  onClose,
  selectedDates,
  defaultHoursForFirstDate,
  existingOverride,
  onSave,
  onDelete,
  saving,
}) {
  const [isClosed, setIsClosed] = useState(false);
  const [openTime, setOpenTime] = useState("09:00");
  const [closeTime, setCloseTime] = useState("17:00");

  // Initialize from existing override or defaults
  useEffect(() => {
    if (existingOverride) {
      setIsClosed(existingOverride.is_closed);
      setOpenTime(existingOverride.open_time || "09:00");
      setCloseTime(existingOverride.close_time || "17:00");
    } else if (defaultHoursForFirstDate) {
      setIsClosed(defaultHoursForFirstDate.closed || false);
      setOpenTime(defaultHoursForFirstDate.open || "09:00");
      setCloseTime(defaultHoursForFirstDate.close || "17:00");
    } else {
      setIsClosed(true);
      setOpenTime("09:00");
      setCloseTime("17:00");
    }
  }, [existingOverride, defaultHoursForFirstDate, isOpen]);

  const dateDisplay = useMemo(() => {
    if (!selectedDates || selectedDates.length === 0) return "";

    const formatDate = (dateStr) => {
      const d = new Date(dateStr + "T00:00:00");
      return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    };

    if (selectedDates.length === 1) {
      return formatDate(selectedDates[0]);
    }

    const sorted = [...selectedDates].sort();
    const first = new Date(sorted[0] + "T00:00:00");
    const last = new Date(sorted[sorted.length - 1] + "T00:00:00");

    return `${first.toLocaleDateString("en-US", { month: "long", day: "numeric" })} - ${last.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} (${selectedDates.length} days)`;
  }, [selectedDates]);

  const defaultDisplay = useMemo(() => {
    if (!defaultHoursForFirstDate) return "Closed";
    if (defaultHoursForFirstDate.closed) return "Closed";
    return `Open ${defaultHoursForFirstDate.open || "?"} - ${defaultHoursForFirstDate.close || "?"}`;
  }, [defaultHoursForFirstDate]);

  const handleSave = () => {
    onSave({
      dates: selectedDates,
      is_closed: isClosed,
      open_time: isClosed ? null : openTime,
      close_time: isClosed ? null : closeTime,
    });
  };

  const handleDelete = () => {
    onDelete(selectedDates);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Edit Override</h3>
          <p className="text-sm text-gray-500 mt-1">{dateDisplay}</p>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-4">
          {/* Default hours reference */}
          <div className="text-sm text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
            Default: {defaultDisplay}
          </div>

          {/* Open/Closed toggle */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">Status:</span>
            <div className="flex rounded-lg overflow-hidden border border-gray-200">
              <button
                type="button"
                onClick={() => setIsClosed(false)}
                className={`px-4 py-2 text-sm font-medium transition ${
                  !isClosed ? "bg-green-500 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                Open
              </button>
              <button
                type="button"
                onClick={() => setIsClosed(true)}
                className={`px-4 py-2 text-sm font-medium transition ${
                  isClosed ? "bg-red-500 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                Closed
              </button>
            </div>
          </div>

          {/* Time selectors (only if open) */}
          {!isClosed && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Opens at</label>
                <TimeSelect value={openTime} onChange={setOpenTime} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Closes at</label>
                <TimeSelect value={closeTime} onChange={setCloseTime} />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
          <div>
            {existingOverride && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={saving}
                className="text-sm text-red-600 hover:text-red-700 font-medium"
              >
                Remove Override
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition disabled:bg-gray-400"
            >
              {saving ? "Saving..." : "Save Override"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
